import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, BackButton, RNLLoader, Card } from '../components/UI';
import { exportCSV } from '../utils';
import * as api from '../api';
import ExpenseRegisterStats from '../components/expense-register/ExpenseRegisterStats';
import ExpenseRegisterFilters from '../components/expense-register/ExpenseRegisterFilters';
import ExpenseRegisterTable from '../components/expense-register/ExpenseRegisterTable';
import { applyFilters, groupLines, buildCsvRows } from '../components/expense-register/erUtils';

const EMPTY_FILTERS = {
  project: '', vendor: '', department: '', paymentMethod: '', accountLedger: '',
  purchaseType: '', status: '', dateFrom: '', dateTo: '', search: '',
};

const sumBy = (rows, key) => rows.reduce((t, r) => t + (Number(r[key]) || 0), 0);
const countDistinct = (rows, key) => new Set(rows.map(r => r[key]).filter(Boolean)).size;

export default function ExpenseRegisterPage({ toast }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allLines, setAllLines] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [groupBy, setGroupBy] = useState('department_name');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getExpenseRegister();
        if (!cancelled) setAllLines(res?.lines || []);
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          toast && toast.show(e.message, 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => applyFilters(allLines, filters), [allLines, filters]);
  const groups = useMemo(() => groupLines(filtered, groupBy), [filtered, groupBy]);

  // Recomputed from the filtered set so the tiles always describe exactly
  // what the table below is showing.
  const stats = useMemo(() => ({
    total_transactions: filtered.length,
    total_amount: sumBy(filtered, 'amount'),
    departments: countDistinct(filtered, 'department_name'),
    vendors: countDistinct(filtered, 'vendor_name'),
    projects: countDistinct(filtered, 'project_name'),
    vouchers: countDistinct(filtered, 'voucher_no'),
  }), [filtered]);

  const grandTotal = useMemo(() => ({
    amount: sumBy(filtered, 'amount'),
    payable: sumBy(filtered, 'amount_payable'),
    paid: sumBy(filtered, 'amount_paid'),
  }), [filtered]);

  const hasFilters = Object.values(filters).some(Boolean);

  const handleExport = () => {
    if (!filtered.length) { toast && toast.show('Nothing to export.', 'error'); return; }
    exportCSV(buildCsvRows(filtered), 'all-in-one-expense-register');
  };

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center' }}><RNLLoader title="Building expense register…" /></div>;
  }

  return (
    // minWidth:0 on a flex column stops any single overflowing child from
    // widening the whole page and taking its siblings with it.
    <div className="fade-in" style={{
      padding: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 16,
      minWidth: 0, maxWidth: '100%',
    }}>
      <div>
        <BackButton onClick={() => navigate('/expense-master')} style={{ marginBottom: 12 }}>
          ← Back to Expense Master
        </BackButton>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontSize: 21, fontWeight: 800, margin: 0 }}>All-In-One Expense Register</h2>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              Master register — every line of every approved and posted payment voucher, with its requisition details. Expandable by group.
            </div>
          </div>
          <Btn onClick={handleExport}>⬇ Export CSV</Btn>
        </div>
      </div>

      {error && (
        <Card style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>
          Could not load the expense register: {error}
        </Card>
      )}

      <ExpenseRegisterStats stats={stats} />

      <ExpenseRegisterFilters
        allLines={allLines}
        filters={filters}
        setFilters={setFilters}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {groups.length} group{groups.length === 1 ? '' : 's'} · {filtered.length} line{filtered.length === 1 ? '' : 's'}
          {hasFilters && ` (filtered from ${allLines.length})`}
        </div>
        {hasFilters && (
          <Btn variant="ghost" size="sm" onClick={() => setFilters(EMPTY_FILTERS)}>Clear filters</Btn>
        )}
      </div>

      <ExpenseRegisterTable groups={groups} groupBy={groupBy} grandTotal={grandTotal} />
    </div>
  );
}
